import {
  PrimaryCta,
  Reveal,
  Section,
  SectionHeading,
  SecondaryCta,
  Seo,
} from '@/components/primitives'
import { MotionVideo } from '@/components/MotionVideo'

const ACCESS_CONTROLS = [
  {
    id: 'ACCT-SCOPE',
    name: 'Account scope',
    detail: 'Everything stays inside your account boundary.',
  },
  {
    id: 'ROLE-ABILITY',
    name: 'Roles and permissions',
    detail: 'You decide what each person can see and do.',
  },
  {
    id: 'SESSION',
    name: 'Safe sign-in',
    detail: 'Server-side sessions with timeout and revoke options.',
  },
  {
    id: 'MFA',
    name: 'MFA and passkeys',
    detail: 'Strong sign-in options with lockout protection.',
  },
  {
    id: 'SSO',
    name: 'Single sign-on',
    detail: 'SAML, Google, and Microsoft sign-in, where configured.',
  },
  {
    id: 'IP-CONTROL',
    name: 'IP rules',
    detail: 'Limit sign-in and API access to your networks.',
  },
  {
    id: 'FIELD-MASK',
    name: 'Field masking',
    detail: 'Sensitive fields stay hidden unless a role allows them.',
  },
  {
    id: 'CREDENTIALS',
    name: 'Credential handling',
    detail: 'Integration credentials handled by platform services.',
  },
]

const EVIDENCE = [
  {
    id: 'AUDIT-LOG',
    name: 'Audit logs',
    detail: 'Every change leaves a trail you can query.',
  },
  {
    id: 'EXPORT-LOG',
    name: 'Export logs',
    detail: 'See what left, who took it, and when.',
  },
  {
    id: 'ACCESS-LOG',
    name: 'Access logs',
    detail: 'Even viewing sensitive data gets logged.',
  },
  {
    id: 'LOGIN-LOG',
    name: 'Sign-in logs',
    detail: 'Sign-ins, failures, and lockouts stay visible.',
  },
  {
    id: 'WF-VERSION',
    name: 'Approvals',
    detail: 'Automation goes live only after review. Runs cite their version.',
  },
  {
    id: 'API-LOG',
    name: 'API logs',
    detail: 'Outside systems follow the same rules and logging.',
  },
]

const MARKS = ['SOC 2 Type 2', 'HIPAA + BAA', 'PCI-DSS', 'CCPA', 'TCPA/DNC', 'Native e-sign']

export default function Security() {
  return (
    <>
      <Seo
        title="Security | TLDNXS Customer Operations Platform"
        description="Control the system. Prove every change. Roles, masking, audit, and approvals built for operators who scale."
      />

      <section aria-labelledby="security-title" className="relative overflow-hidden">
        <MotionVideo
          src="/videos/control-plane-web.mp4"
          poster="/videos/control-plane-poster.jpg"
          className="pointer-events-none absolute inset-0 opacity-20"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-base-950/75 via-base-950/60 to-base-950"
        />
        <div
          aria-hidden="true"
          className="field-grid pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 md:py-28">
          <Reveal>
            <div className="mx-auto max-w-3xl text-center">
            <p className="eyebrow">Security and governance</p>
            <h1
              id="security-title"
              className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl"
            >
              Control the system. Prove every change.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-mute md:text-lg">
              Decide who sees what. Track every change. Keep the evidence. Governance is how
              serious teams scale, not a footnote on the security page.
            </p>
            </div>
          </Reveal>
        </div>
      </section>

      <Section id="access" labelledBy="access-heading" className="bg-base-900/40">
        <Reveal>
          <SectionHeading
            id="access-heading"
            eyebrow="Access control"
            title="Access on your terms."
          />
        </Reveal>
        <Reveal delay={100}>
          <ol className="mt-12 grid gap-px overflow-hidden rounded-sm border border-base-line bg-base-line md:grid-cols-2">
            {ACCESS_CONTROLS.map((c) => (
              <li key={c.id} className="bg-base-850 p-6">
                <span className="chip-neutral">{c.id}</span>
                <p className="mt-3 text-base font-extrabold tracking-tight text-ink">{c.name}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-mute">{c.detail}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      </Section>

      <Section id="audit" labelledBy="audit-heading">
        <Reveal>
          <SectionHeading
            id="audit-heading"
            eyebrow="Evidence"
            title="If it happened, there is a log."
            copy="Governance is not a slogan. It is a record you can hand to the people who ask."
          />
        </Reveal>
        <Reveal delay={100}>
          <ol className="mt-12 grid gap-px overflow-hidden rounded-sm border border-base-line bg-base-line sm:grid-cols-2 lg:grid-cols-3">
            {EVIDENCE.map((e) => (
              <li key={e.id} className="bg-base-850 p-6">
                <span className="chip-neutral">{e.id}</span>
                <p className="mt-3 text-base font-extrabold tracking-tight text-ink">{e.name}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-mute">{e.detail}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      </Section>

      <Section labelledBy="marks-heading" className="bg-base-900/40">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionHeading
                id="marks-heading"
                eyebrow="Trust marks"
                eyebrowTone="mute"
                title="Trust marks you can take into procurement."
                copy="These marks reflect our published trust posture. We review scope and current evidence with your team during evaluation."
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={100}>
              <ul
                aria-label="Trust and compliance marks"
                className="flex flex-wrap items-center justify-center gap-3"
              >
                {MARKS.map((mark) => (
                  <li
                    key={mark}
                    className="rounded-sm border border-base-linebright bg-base-850 px-4 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-ink-mute"
                  >
                    {mark}
                  </li>
                ))}
              </ul>
              <ul className="mt-8 space-y-4">
                {[
                  'Consent, Do-Not-Call, and calling-window checks are operational controls, not legal advice.',
                  'Retention and deletion behavior is scoped per deployment.',
                  'Capacity and architecture are defined during evaluation.',
                ].map((line) => (
                  <li
                    key={line}
                    className="border-l-2 border-warn/60 pl-4 text-sm leading-relaxed text-ink-mute"
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section labelledBy="security-cta-heading">
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <h2
              id="security-cta-heading"
              className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
            >
              Bring your security questions.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-ink-mute md:text-lg">
              We will walk through the controls with your team.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <PrimaryCta to="/walkthrough">Book a walkthrough</PrimaryCta>
              <SecondaryCta to="/platform">Explore the platform</SecondaryCta>
            </div>
          </div>
        </Reveal>
      </Section>
    </>
  )
}
